import { useMemo } from 'react';

import { STUDIES_TO_SHOW_REGION } from '../components/PointOfInterestCountryList/constants';

import { useFilterMapData } from './use-filter-poi-map-data';
import { useCaseStudiesData } from './use-case-studies-data';

export interface ProgrammeHierarchyItem {
  programmeId: string;
  country: string;
  city: string;
  region?: string;
  studyName?: string;
}

export interface CaseStudyNavigationItem {
  caseStudyId: string;
  title: string;
  country: string;
  city: string;
  region?: string;
  studyName?: string;
  studyAcronym?: string;
  programme_id: string;
}

export interface SubdivisionData {
  name: string;
  programmes: ProgrammeHierarchyItem[];
  isRegion: boolean;
  studyCount: number;
}

export interface CountryData {
  name: string;
  subdivisions: SubdivisionData[];
  totalStudyCount: number;
  hasLargeStudySet: boolean;
}

export interface PoiHierarchy {
  countries: CountryData[];
  flattenedProgrammes: ProgrammeHierarchyItem[];
  totalProgrammeCount: number;
}

/**
 * Hook that provides structured POI data hierarchy matching UI display order
 * Used by both the country list UI and navigation logic
 */
export const usePoiHierarchy = (): PoiHierarchy => {
  const { filteredPoiStudyData, filteredStudiesById } = useFilterMapData();

  // Pre-compute country names for performance
  const countryNames = useMemo(
    () =>
      filteredPoiStudyData
        ? [
            ...new Set(
              filteredPoiStudyData.studies.map(study => study.country),
            ),
          ].sort((a, b) => (a < b ? -1 : 1))
        : [],
    [filteredPoiStudyData],
  );

  return useMemo(() => {
    if (!filteredPoiStudyData || !filteredStudiesById) {
      return {
        countries: [],
        flattenedProgrammes: [],
        totalProgrammeCount: 0,
      };
    }

    const countries: CountryData[] = [];
    const flattenedProgrammes: ProgrammeHierarchyItem[] = [];

    for (const countryName of countryNames) {
      const countryStudies = filteredPoiStudyData.studies.filter(
        study => study.country === countryName,
      );
      const totalStudyCount = countryStudies.length;
      const hasLargeStudySet = totalStudyCount > STUDIES_TO_SHOW_REGION;

      // Get subdivisions (regions or cities) in UI order
      const subdivisionNames = [
        ...new Set(
          countryStudies.map(study =>
            hasLargeStudySet ? study.region : study.city,
          ),
        ),
      ].sort((a, b) => {
        // "National" first, then alphabetical (matching UI logic)
        if (a.toLowerCase() === 'national') return -1;
        if (b.toLowerCase() === 'national') return 1;
        return a < b ? -1 : 1;
      });

      const subdivisions: SubdivisionData[] = [];

      for (const subdivisionName of subdivisionNames) {
        // Get studies for this subdivision, preserving original order
        const subdivisionStudies = countryStudies.filter(study => {
          const subdivisionValue = hasLargeStudySet ? study.region : study.city;
          return subdivisionValue === subdivisionName;
        });

        const programmes: ProgrammeHierarchyItem[] = [];

        for (const study of subdivisionStudies) {
          const programme = filteredStudiesById[study.id];
          if (programme) {
            const hierarchyItem: ProgrammeHierarchyItem = {
              programmeId: study.id,
              country: programme.country,
              city: programme.city,
              region: programme.region,
              studyName: programme.studyName,
            };

            programmes.push(hierarchyItem);
            flattenedProgrammes.push(hierarchyItem);
          }
        }

        subdivisions.push({
          name: subdivisionName,
          programmes,
          isRegion: hasLargeStudySet,
          studyCount: programmes.length,
        });
      }

      countries.push({
        name: countryName,
        subdivisions,
        totalStudyCount,
        hasLargeStudySet,
      });
    }

    return {
      countries,
      flattenedProgrammes,
      totalProgrammeCount: flattenedProgrammes.length,
    };
  }, [filteredPoiStudyData, filteredStudiesById, countryNames]);
};

/**
 * Hook that provides just the flattened navigation array
 * Optimized for navigation use cases
 */
export const usePoiNavigationArray = (): ProgrammeHierarchyItem[] => {
  const hierarchy = usePoiHierarchy();
  return useMemo(
    () => hierarchy.flattenedProgrammes,
    [hierarchy.flattenedProgrammes],
  );
};

/**
 * Hook that provides country-grouped data
 * Optimized for country list UI use cases
 */
export const usePoiCountryHierarchy = (): {
  countries: CountryData[];
  totalProgrammeCount: number;
} => {
  const hierarchy = usePoiHierarchy();
  return useMemo(
    () => ({
      countries: hierarchy.countries,
      totalProgrammeCount: hierarchy.totalProgrammeCount,
    }),
    [hierarchy.countries, hierarchy.totalProgrammeCount],
  );
};

/**
 * Hook that provides case studies navigation array
 * Follows the same hierarchical ordering as implementation studies
 */
export const useCaseStudiesNavigationArray = (): CaseStudyNavigationItem[] => {
  const { flattenedProgrammes } = usePoiHierarchy();
  const { caseStudies } = useCaseStudiesData();

  return useMemo(() => {
    if (!caseStudies || !flattenedProgrammes) {
      return [];
    }

    const navigationItems: CaseStudyNavigationItem[] = [];

    // Follow the same order as implementation studies
    for (const programme of flattenedProgrammes) {
      // Find all case studies for this programme, in JSON order
      const programmeCaseStudies = caseStudies.filter(
        caseStudy => caseStudy.linkedProgrammeId === programme.programmeId,
      );

      for (const caseStudy of programmeCaseStudies) {
        navigationItems.push({
          caseStudyId: caseStudy.id,
          title: caseStudy.title,
          country: caseStudy.country,
          city: caseStudy.city,
          region: caseStudy.region,
          studyName: caseStudy.studyName,
          studyAcronym: caseStudy.studyAcronym,
          programme_id: caseStudy.linkedProgrammeId,
        });
      }
    }

    return navigationItems;
  }, [flattenedProgrammes, caseStudies]);
};
